import React, { useState, useEffect } from 'react';
import { Bell, Heart, MessageCircle, User, Check } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../lib/engagementService';
import { FollowButton } from '../components/profile/FollowButton';

interface NotificationItemProps {
  notification: any;
  onMarkAsRead: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onMarkAsRead }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'like':
        return <Heart className="h-6 w-6 text-red-500" />;
      case 'comment':
        return <MessageCircle className="h-6 w-6 text-purple-600" />;
      case 'follow':
        return <User className="h-6 w-6 text-blue-500" />;
      case 'comment_like':
        return <Heart className="h-6 w-6 text-pink-500" />;
      default:
        return <Bell className="h-6 w-6 text-gray-500" />;
    }
  };

  const getNotificationText = () => {
    switch (notification.type) {
      case 'like':
        return 'liked your post';
      case 'comment':
        return 'commented on your post';
      case 'follow':
        return 'started following you';
      case 'comment_like':
        return 'liked your comment';
      default:
        return 'interacted with your content';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    return `${Math.floor(diffInSeconds / 86400)}d`;
  };

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
  };

  return (
    <div 
      className={`flex items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
        !notification.is_read ? 'bg-purple-50' : 'bg-white'
      }`}
      onClick={handleClick}
    >
      <div className="mr-3">
        {getIcon()}
      </div>
      
      <div className="flex-grow">
        <div className="flex items-center">
          <img
            src={notification.actor.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300'}
            alt={notification.actor.username}
            className="h-8 w-8 rounded-full object-cover mr-3"
          />
          <div className="flex-1">
            <p className="text-sm">
              <span className="font-medium mr-1">{notification.actor.username}</span>
              <span className="text-gray-600">{getNotificationText()}</span>
              <span className="ml-2 text-xs text-gray-500">
                {formatTimeAgo(notification.created_at)}
              </span>
            </p>
            {notification.comment && (
              <p className="text-xs text-gray-500 mt-1 truncate">
                "{notification.comment.content}"
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="ml-4 flex items-center space-x-2">
        {notification.type === 'follow' ? (
          <FollowButton
            userId={notification.actor.id}
            username={notification.actor.username}
            size="sm"
          />
        ) : notification.post && notification.post.post_media && notification.post.post_media.length > 0 && (
          <div className="w-10 h-10 rounded overflow-hidden">
            <img 
              src={notification.post.post_media[0].media_url}
              alt="Post"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
        
        {!notification.is_read && (
          <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
        )}
      </div>
    </div>
  );
};

export const NotificationsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'follows'>('all');

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await getNotifications(user?.id || '');
      setNotifications(data);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true }
            : notif
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(user?.id || '');
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, is_read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const filteredNotifications = activeTab === 'all' 
    ? notifications 
    : notifications.filter(notification => notification.type === 'follow');

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 max-w-screen-md">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 pb-16 md:pt-20 md:pb-0 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-screen-md">
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center">
              <Bell className="h-5 w-5 mr-2" />
              <h1 className="text-lg font-medium">Notifications</h1>
              {unreadCount > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="flex items-center text-sm text-purple-600 hover:text-purple-700 transition-colors"
              >
                <Check className="h-4 w-4 mr-1" />
                Mark all read
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button 
              className={`flex-1 py-3 font-medium text-sm ${
                activeTab === 'all' 
                  ? 'text-purple-600 border-b-2 border-purple-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('all')}
            >
              All
            </button>
            <button 
              className={`flex-1 py-3 font-medium text-sm ${
                activeTab === 'follows' 
                  ? 'text-purple-600 border-b-2 border-purple-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('follows')}
            >
              Follows
            </button>
          </div>
          
          {/* Notifications List */}
          <div>
            {filteredNotifications.length > 0 ? (
              filteredNotifications.map((notification) => (
                <NotificationItem 
                  key={notification.id} 
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                />
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No notifications yet</p>
                <p className="text-sm">
                  {activeTab === 'all' 
                    ? "When someone likes or comments on your posts, you'll see it here."
                    : "When someone follows you, you'll see it here."
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};